import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { redirectToCheckout, getSafeStripeReturnPath } from "../../services/stripeService";
import { useStripePrices } from "../../services/priceConfig";
import backendClient from "../../services/backendClient";
import LoginModal from "../LoginModal/LoginModal";
import { ArrowRight, Check, CreditCard, Sparkles } from "lucide-react";

function Premium() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isYearly, setIsYearly] = useState(true); // Payment mode toggle state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const isLoggedIn = !!localStorage.getItem('authToken');
  const prices = useStripePrices();
  const trialDays = prices.trialDays ?? 5;
  // trialEligible: logged-in users whose hasUsedFreeTrial flag is false, or non-logged-in users
  const trialEligible = !isLoggedIn || !hasUsedTrial;
  const afterTrialCopy = isYearly
    ? t("premium.afterTrialAnnual", { price: prices.yearlyPerMonth, total: prices.yearlyTotal })
    : t("premium.afterTrialMonthly", { price: prices.monthlyPerMonth });
  const trialHighlights = [
    { Icon: CreditCard, label: t("premium.trialNoCharge") },
    { Icon: Sparkles, label: t("premium.trialFullAccess") },
  ];
  const features = [
    t("premium.feature1"),
    t("premium.feature2"),
    t("premium.feature3"),
    t("premium.feature4"),
    t("premium.feature5"),
  ];

  // Fetch trial eligibility for logged-in users on mount
  useEffect(() => {
    if (!isLoggedIn) return;
    backendClient.get('/api/user/me').then((userData) => {
      if (userData?.hasUsedFreeTrial) {
        setHasUsedTrial(true);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user is already premium — returns true and redirects if so
  const checkPremiumAndRedirect = async () => {
    try {
      const userData = await backendClient.get('/api/user/me');
      if (userData?.isPremium) {
        console.log('[Premium] User already has premium, redirecting to wizard');
        navigate('/wizard');
        return true;
      }
    } catch (err) {
      console.error('[Premium] Error checking premium status:', err);
    }
    return false;
  };

  // Auto-proceed to checkout if user just returned from OAuth
  useEffect(() => {
    const pending = localStorage.getItem('oauthPendingCheckout');
    if (pending) {
      localStorage.removeItem('oauthPendingCheckout');
      const token = localStorage.getItem('authToken');
      if (token) {
        // Check premium status before proceeding to checkout
        checkPremiumAndRedirect().then((alreadyPremium) => {
          if (!alreadyPremium) {
            proceedToCheckout();
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const proceedToCheckout = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await redirectToCheckout({
        isYearly,
        returnPath: getSafeStripeReturnPath(searchParams.get('returnTo')),
      });
    } catch (err) {
      console.error('[Premium] Error starting checkout:', err);
      setError(t('premium.checkoutError'));
      setIsLoading(false);
    }
  };

  const handleStartPremium = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // User not logged in — show login modal first
      setIsLoginModalOpen(true);
      return;
    }
    // User already logged in — check premium before checkout
    const alreadyPremium = await checkPremiumAndRedirect();
    if (!alreadyPremium) {
      await proceedToCheckout();
    }
  };

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    
    // Check if user is already premium — if so, redirect instead of checkout
    const alreadyPremium = await checkPremiumAndRedirect();
    if (!alreadyPremium) {
      // Re-check trial eligibility now that user is logged in
      try {
        const userData = await backendClient.get('/api/user/me');
        if (userData?.hasUsedFreeTrial) setHasUsedTrial(true);
      } catch (_) {}
      await proceedToCheckout();
    }
  };
  
  return (
    <>
      <main className="min-h-screen bg-[#F8F8F7] md:pt-[60px]">
        <div className="max-w-5xl mx-auto md:px-5 px-5 py-8 md:py-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <section className="max-w-2xl">
              {trialEligible && (
                <div className="flex justify-center md:justify-start mb-5">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#FFCB00] px-4 py-2 text-[#0D0C07] text-[13px] font-semibold leading-[18px]">
                    <Sparkles className="size-4" aria-hidden="true" />
                    {t("premium.trialBadge", { days: trialDays })}
                  </div>
                </div>
              )}

              <h1 className="text-center md:text-left text-[#0D0C07] text-[32px] md:text-[40px] font-bold leading-[40px] md:leading-[56px] tracking-[0] mb-4">
                {t('premium.subTitleTop', { days: trialDays })}
              </h1>

              <p className="text-center md:text-left text-[#56554D] text-[16px] md:text-[18px] leading-[28px] max-w-xl md:mb-7">
                {trialEligible
                  ? t('premium.subTitleMain', { days: trialDays })
                  : t('premium.becomePremium')}
              </p>

              {trialEligible && (
                <div className="hidden w-full lg:grid gap-3 mb-0 lg:mb-0">
                  {trialHighlights.map(({ Icon, label }) => (
                    <div key={label} className="rounded-[8px] bg-white border border-[#E2E1DF] px-4 py-3 shadow-[0_1px_2px_rgba(35,33,22,0.04)]">
                      <Icon className="size-5 text-[#518C03] mb-2" aria-hidden="true" />
                      <p className="text-[#0D0C07] text-[13.2px] font-semibold leading-[20px]">
                        {label}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-[12px] md:p-6 p-5 shadow-[0px_1px_2px_0px_rgba(35,33,22,0.02),4px_12px_28px_0px_rgba(83,81,69,0.14)] border border-[#E2E1DF]">
              <div className="relative flex w-full bg-[#F3F3F2] rounded-[32px] p-1 mb-6">
                <div
                  className={`
                    absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-white rounded-[28px]
                    transition-transform duration-300 ease-in-out shadow-sm
                    ${isYearly ? "translate-x-full" : "translate-x-0"}
                  `}
                />
                <button
                  type="button"
                  onClick={() => setIsYearly(false)}
                  className={`
                    relative z-10 flex-1 min-w-0 py-2.5 px-2 text-[14px] font-semibold rounded-[28px]
                    transition-colors duration-300 ease-in-out text-center
                    ${!isYearly ? "text-[#0D0C07]" : "text-[#65635D]"}
                  `}
                >
                  {t("premium.monthly")}
                </button>
                <button
                  type="button"
                  onClick={() => setIsYearly(true)}
                  className={`
                    relative z-10 flex-1 min-w-0 py-2.5 px-2 text-[14px] font-semibold rounded-[28px]
                    transition-colors duration-300 ease-in-out flex items-center justify-center gap-1.5
                    ${isYearly ? "text-[#0D0C07]" : "text-[#65635D]"}
                  `}
                >
                  <span className="truncate">{t("premium.annual")}</span>
                  <span className="text-[10px] leading-[14px] font-semibold text-[#0D0C07] bg-[#FFCB00] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {t("premium.save10")}
                  </span>
                </button>
              </div>

              <div className="rounded-[10px] bg-[#F8F8F7] border border-[#E8E7E3] p-5 mb-6">
                {trialEligible ? (
                  <>
                    <p className="text-[#518C03] text-[13px] leading-[20px] font-semibold mb-2">
                      {t("premium.trialEyebrow", { days: trialDays })}
                    </p>
                    <div className="flex items-end gap-2 mb-2">
                      <span className="text-[#0D0C07] text-[52px] leading-[56px] font-bold tracking-[0]">
                        {t("premium.trialPrice")}
                      </span>
                      <span className="text-[#56554D] text-[14px] leading-[22px] pb-2">
                        {t("premium.trialPriceSuffix", { days: trialDays })}
                      </span>
                    </div>
                    <p className="text-[#56554D] text-[14px] leading-[24px]">
                      {afterTrialCopy}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[#518C03] text-[24px] leading-[32px] font-light mb-1">
                      {isYearly
                        ? t("premium.onlyPrice", { price: prices.yearlyPerMonth })
                        : t("premium.monthlyPrice", { price: prices.monthlyPerMonth })}
                    </p>
                    <p className={`text-[15.2px] font-normal leading-[28px] text-[#0D0C07] ${isYearly ? '' : 'invisible'}`}>
                      {t("premium.yearPriceTotal", { price: prices.yearlyTotal })}
                    </p>
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={handleStartPremium}
                disabled={isLoading}
                className={`
                  w-full inline-flex justify-center items-center gap-2 h-[52px] px-[24px]
                  rounded-[32px] bg-[#090] text-[#FFF] text-center text-[14.2px]
                  font-semibold leading-[28px] transition-colors
                  md:hover:bg-[#060]
                  shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                  ${isLoading ? "opacity-70 cursor-wait" : ""}
                `}
              >
                {isLoading ? t('premium.loading') : trialEligible
                  ? t("premium.becomePremiumButton", { days: trialDays })
                  : t("premium.subscribeButton")}
                <ArrowRight className="size-5" aria-hidden="true" />
              </button>

              <p className="text-[#56554D] text-[13.2px] leading-[20px] mt-4 text-center">
                {trialEligible
                  ? t("premium.cancelAnytime", { days: trialDays })
                  : t("premium.cancelAnytimeSimple")}
              </p>
              {error && (
                <p className="text-[#DC2626] text-[13.2px] leading-[20px] mt-2 text-center">
                  {error}
                </p>
              )}

              <div className="h-px bg-[#E8E7E3] my-6" />

              <div className="flex flex-col gap-4">
                {features.map((feature) => (
                  <div key={feature} className="flex gap-3 items-start">
                    <div className="bg-[#F3F3F2] flex items-center justify-center rounded-[6px] size-[28px] shrink-0">
                      <Check className="size-4 text-[#0D0C07]" strokeWidth={2} aria-hidden="true" />
                    </div>
                    <span className="text-[#0D0C07] text-[14.2px] leading-[24px]">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Sign In CTA */}
        {!isLoggedIn && (
          <div className="max-w-lg mx-auto px-6 pt-4 pb-16 text-center">
            <h3 className="text-[#0d0c07] text-[18px] font-semibold leading-[28px] mb-4">
              {t('premium.alreadyHaveAccount')}
            </h3>
            <button
              onClick={() => setIsSignInModalOpen(true)}
              className="
                inline-flex justify-center items-center gap-1
                h-[48px] px-[32px] py-[14px]
                rounded-[32px]
                bg-[#0D0C07]
                text-white text-center
                text-[14.2px] font-semibold leading-[28px]
                transition-colors
                md:hover:bg-[#1a1812]
              "
            >
              {t('premium.signIn')}
            </button>
          </div>
        )}
      </main>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        context="premium"
      />

      <LoginModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
        onLoginSuccess={async () => {
          setIsSignInModalOpen(false);
          const alreadyPremium = await checkPremiumAndRedirect();
          if (!alreadyPremium) {
            navigate('/mymeals');
          }
        }}
        context="premium"
      />
    </>
  );
}

export default Premium;
